<template>
  <div>
    <v-dialog v-model="dialog" persistent max-width="560">
      <v-card class="v-card-custom-dialog">
        <v-card-title class="headline white--text information" primary-title>
          {{ $t('msg.otp.adminTitle') }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="font-bold text-lg mb-3">{{ target.usrId }}</div>

          <div v-if="loading" class="text-center py-4">
            <v-progress-circular indeterminate size="28"></v-progress-circular>
          </div>
          <!-- 조회 실패(권한 없음, 본인 계정 등)는 비활성과 구분해서 서버 사유를 그대로 표시 -->
          <div v-else-if="loadError" class="error--text">{{ loadError }}</div>
          <div v-else-if="!status.enabled">{{ $t('msg.otp.adminNotEnabled') }}</div>
          <template v-else>
            <v-simple-table dense class="mb-4">
              <tbody>
                <tr>
                  <td>{{ $t('msg.otp.adminTitle') }}</td>
                  <td>{{ status.registered ? $t('msg.otp.adminRegistered') : $t('msg.otp.adminNotRegistered') }}</td>
                </tr>
                <tr>
                  <td>{{ $t('msg.otp.adminRegDt') }}</td>
                  <td>{{ status.regDt || '-' }}</td>
                </tr>
                <tr>
                  <td>{{ $t('msg.otp.adminLocked') }}</td>
                  <td :class="status.locked ? 'error--text' : ''">
                    {{ status.locked ? $t('msg.otp.adminLocked') : $t('msg.otp.adminNotLocked') }}
                  </td>
                </tr>
                <tr>
                  <td>{{ $t('msg.otp.adminFailCnt') }}</td>
                  <td>{{ status.failCnt }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-textarea
              v-model="reason"
              :label="$t('msg.otp.adminReason')"
              counter="500"
              maxlength="500"
              rows="2"
              outlined
              dense
              hide-details="auto"
            ></v-textarea>
            <!-- 본인: 현재 인증 앱 코드로 재확인 (비밀번호·본인확인 체크 없음) -->
            <template v-if="status.self">
              <div class="mt-3 text-sm">{{ $t('msg.otp.selfResetGuide') }}</div>
              <v-text-field
                v-model="otpCode"
                :label="$t('msg.otp.selfCode')"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
                outlined
                dense
                hide-details
                class="mt-3"
                @input="otpCode = String($event || '').replace(/\D/g, '').slice(0, 6)"
              ></v-text-field>
            </template>
            <template v-else>
              <v-checkbox
                v-model="identityVerified"
                :label="$t('msg.otp.adminIdentityVerified')"
                hide-details
                class="mt-2"
              ></v-checkbox>
              <v-text-field
                v-model="adminPwd"
                :label="$t('msg.otp.adminPassword')"
                type="password"
                autocomplete="current-password"
                outlined
                dense
                hide-details
                class="mt-4"
              ></v-text-field>
            </template>
            <div v-if="message" class="mt-3 text-sm" :class="messageError ? 'error--text' : ''">
              {{ message }}
            </div>
          </template>
        </v-card-text>
        <v-divider class="deviderColor"></v-divider>
        <v-card-actions class="btn-pos">
          <v-spacer></v-spacer>
          <v-btn
            v-if="status.enabled && !status.self && !signedOut"
            class="btn-height-custom mb-0"
            :disabled="!canSubmit || !status.locked"
            @click="confirm('UNLOCK')"
          >
            {{ $t('msg.otp.adminUnlock') }}
          </v-btn>
          <v-btn
            v-if="status.enabled && !signedOut"
            class="btn-height-custom btnConfirm mb-0"
            :disabled="!canSubmit || (!status.registered && !status.locked)"
            @click="confirm('RESET')"
          >
            {{ $t('msg.otp.adminReset') }}
          </v-btn>
          <v-btn class="btn-height-custom mb-0 btnCancel" :disabled="submitting" @click="close()">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <dialogWindow
      ref="confirmDialog"
      size="max"
      color="confirmation"
      :info="target.usrId"
      :detail="confirmMessage"
      :showCancel="true"
      @select-ok="execute()"
    ></dialogWindow>
  </div>
</template>

<script>
import dialogWindow from '@/vue-dlb/components/NewDialog.vue';

/**
 * 관리자 OTP 초기화 / 잠금 해제 (clt-topt-standard.md 9장).
 * 서버가 권한·대상 범위·비밀번호(본인은 현재 OTP 코드)를 다시 확인한다.
 * 비밀키·QR 은 관리자에게도 표시하지 않는다.
 * 본인 초기화 성공 시 현재 세션도 종료되므로 로그인 화면으로 이동한다.
 */
export default {
  components: {
    dialogWindow,
  },

  data: () => ({
    dialog: false,
    loading: false,
    submitting: false,
    target: { usrId: '' },
    status: { enabled: false },
    loadError: '',
    reason: '',
    identityVerified: false,
    adminPwd: '',
    otpCode: '',
    action: '',
    message: '',
    messageError: false,
    signedOut: false,
  }),

  computed: {
    canSubmit() {
      if (this.submitting || this.signedOut || !this.reason.trim()) return false;
      if (this.status.self) return /^\d{6}$/.test(this.otpCode);
      return this.identityVerified && !!this.adminPwd;
    },
    confirmMessage() {
      if (this.action !== 'RESET') return this.$t('msg.otp.adminUnlockConfirm');
      return this.status.self
        ? this.$t('msg.otp.selfResetConfirm')
        : this.$t('msg.otp.adminResetConfirm');
    },
  },

  methods: {
    async open(user) {
      this.target = { usrId: user.usrId };
      this.status = { enabled: false };
      this.reason = '';
      this.identityVerified = false;
      this.adminPwd = '';
      this.otpCode = '';
      this.message = '';
      this.signedOut = false;
      this.dialog = true;
      await this.loadStatus();
    },

    async loadStatus() {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await this.$store.dispatch('getOtpStatus', { usrId: this.target.usrId });
        this.status = res || { enabled: false };
      } catch (err) {
        this.status = { enabled: false };
        this.loadError = this.errorText(err);
      } finally {
        this.loading = false;
      }
    },

    confirm(action) {
      this.action = action;
      this.$refs.confirmDialog.dialog = true;
    },

    async execute() {
      if (!this.canSubmit) return;
      this.submitting = true;
      const params = this.status.self
        ? { usrId: this.target.usrId, reason: this.reason.trim(), otpCode: this.otpCode }
        : {
          usrId: this.target.usrId,
          reason: this.reason.trim(),
          identityVerified: this.identityVerified,
          adminPwd: this.adminPwd,
        };
      try {
        const res = await this.$store.dispatch(this.action === 'RESET' ? 'resetOtp' : 'unlockOtp', params);
        if (res && res.success && res.self) {
          // 본인 세션이 종료됐으므로 더 이상 API 호출 없이 로그인 화면으로 안내
          this.signedOut = true;
          this.showMessage(this.$t('msg.otp.selfResetDone'), false);
        } else if (res && res.success) {
          const notes = [this.action === 'RESET' ? this.$t('msg.otp.adminResetDone') : this.$t('msg.otp.adminUnlockDone')];
          if (res.mailSent === false) notes.push(this.$t('msg.otp.adminMailFailed'));
          if (res.sessionsTerminated === -1) notes.push(this.$t('msg.otp.adminSessionFailed'));
          this.reason = '';
          this.identityVerified = false;
          await this.loadStatus();
          this.showMessage(notes.join(' '), notes.length > 1);
        } else {
          this.showMessage((res && res.message) || 'Failed.', true);
        }
      } catch (err) {
        this.showMessage(this.errorText(err), true);
      } finally {
        this.adminPwd = '';
        this.otpCode = '';
        this.submitting = false;
      }
    },

    errorText(err) {
      return (err && err.response && typeof err.response.data === 'string' && err.response.data) || 'Failed.';
    },

    showMessage(message, isError) {
      this.message = message;
      this.messageError = isError;
    },

    close() {
      this.adminPwd = '';
      this.otpCode = '';
      this.dialog = false;
      if (this.signedOut) this.goLogin();
    },

    goLogin() {
      this.$cookies.remove('hash');
      // Modify User 는 팝업 창이므로 원래 창을 로그인 화면으로 보내고 팝업은 닫는다
      if (window.opener && !window.opener.closed) {
        window.opener.location.href = '/login';
        window.close();
      } else {
        window.location.href = '/login';
      }
    },
  },
};
</script>
